/**
 * Every visible string on the users list, taken verbatim from the legacy resources.
 * Keys are noted so they can be swapped for a real i18n lookup later.
 */
export const strings = {
  /* Fallbacks for an action outcome. The server almost always sends its own
     sentence — a duplicate username, the policy a password missed — and that is
     what gets shown; these cover the case where it sends none. */
  actionDone: 'تمت العملية بنجاح',
  actionFailed: 'تعذر إتمام العملية، فضلا حاول مرة أخرى',
  fail: {
    action: 'تعذر إتمام العملية على هذا المستخدم',
    directory: 'تعذر استيراد المستخدمين من الربط الآلي',
  },
  // Admin/Users.ar.resx
  title: 'قائمة المستخدمين', // PageResource1.Title
  subtitle: 'قم باختيار مستخدم من اجل تعديل بيانات ملفه الشخصي', // MainPanelSubHeader1
  /* Create and edit both: a name of only dots or symbols is not a name (nameRule.js). */
  nameNeedsLetters: 'يجب أن يحتوي الاسم على حروف',

  tabs: {
    ACTIVE: 'المفعلين', // Resources.Active
    /* Everyone who cannot sign in — suspended, locked, expired or resigned (FilterUsersHandler
       SUSPENDED). "الموقوفين" alone hid the expired accounts listed under it (Taiga #1522). */
    SUSPENDED: 'الموقوفين ومنتهي الصلاحية', // Resources.Suspended
    WAITING_ACTIVATION_APPROVAL: 'الانتظار', // Resources.Waiting_Activation
    IDEAL_USERS: 'لم يستخدموا النظام', // Resources.LabelInActiveUsers
  },

  columns: {
    index: 'م', // Users.UsersList_HeaderText1
    userName: 'اسم المستخدم', // Users.UsersList_HeaderText2
    name: 'الاسم الكامل', // General.FullNameLabel
    email: 'البريد الإلكتروني', // Users.UsersList_HeaderText5
    groups: 'الإدارة', // Users.UsersList_HeaderText6
    status: 'الحالة', // Resources.Status
  },

  actions: {
    add: 'إضافة', // Resources.Add
    createLocalUser: 'إنشاء مستخدم (محلي)', // Resources.CreateLocalUser
    importFromExcelLocal: 'استيراد من ملف إكسل (محلي)', // Resources.ImportFromExcelLocal
    importFromExcelAd: 'استيراد من ملف إكسل (الدليل النشط)', // Resources.ImportFromExcelActiveDirectory
    update: 'تحديث', // Resources.Update
    syncDataSource: 'سحب وتزامن البيانات', // Resources.Fetch_and_synchronize + Resources.Data
    importResigned: 'تحديث بيانات المستقيلين من ملف اكسل', // Resources.UpdateTheDataOfResigned
    resetPasswords: 'إعادة تعيين كلمات مرور المستخدمين', // Resources.ResetPasswordForUsers
    filter: 'بحث', // Resources.Filter
  },

  /** سحب المستخدمين من مصدر الربط — `/Users/Clone`. */
  clone: {
    title: 'سحب المستخدمين من مصدر الربط',
    hint: 'إضافة المستخدمين الموجودين في مصدر الربط إلى قائمة المستخدمين',
    source: 'مصدر الربط',
    sourcePlaceholder: 'اختر مصدر الربط',
    note: 'تطبق العملية وضع ربط المستخدمين المحدد في إعدادات مزامنة البيانات. الاستيراد يضيف حسابات جديدة، والمزامنة تحدث حسابات الدليل الموجودة، والاستيراد والمزامنة ينفذان العمليتين. تطبق إعدادات الاستقالة الخاصة بالمصدر عند المزامنة.',
    submit: 'سحب المستخدمين',
    running: 'فضلا انتظر',
  },

  /** جلب بيانات المستخدم من مصدر الربط — `/Users/GetUserInfoFromDataSource`. */
  fetch: {
    action: 'جلب البيانات من مصدر الربط',
    hint: 'اكتب اسم المستخدم ثم اجلب بياناته من مصدر الربط لتعبئة الحقول',
    running: 'جاري الجلب',
    usernameRequired: 'اكتب اسم المستخدم أولا',
    filled: 'تم جلب بيانات المستخدم',
    notFound: 'لم يتم العثور على هذا المستخدم في مصدر الربط',
  },

  filter: {
    groupLabel: 'الإدارات:', // Resources.Groups
    groupPlaceholder: 'الإدارة', // data-placeholder
    typeLabel: 'النوع:', // Resources.Type
    typePlaceholder: 'نوع المستخدم', // data-placeholder
    adminsOnly: 'مدراء النظام فقط', // Resources.AdminsOnly
    usersOnly: 'المستخدمين فقط', // Resources.UsersOnly
    cancel: 'إلغاء', // Resources.Reset
    submit: 'بحث', // Resources.Filter
  },

  row: {
    edit: 'تعديل البيانات الشخصية', // Users.btnEdit — the legacy reuses this for both row buttons
    /* The lock button opened the status dialog under the SAME label as the pencil beside
       it, so the two buttons announced themselves identically. */
    status: 'حالة الحساب',
    delete: 'حذف المستخدم', // Users.btnDelete
    /* Deleting is irreversible — `DeleteUserHandler` removes the identity user outright —
       so the dialog says what goes, not just "are you sure". */
    deleteConfirm: (name) =>
      `سيتم حذف حساب ${name} نهائيا مع صلاحياته وسجل دخوله. لا يمكن التراجع عن هذه العملية.`,
  },

  /* The two export labels and their failure message went with the export menu
     itself, which this screen no longer offers. */
  footer: {
    deleteInactive: 'حذف', // Groups.btnDelete
  },

  /** #add_modal — mode A, `showCreationForm(false)` */
  create: {
    title: 'إنشاء مستخدم (محلي)', // Resources.CreateLocalUser
    hint: 'هذه الخاصية تستخدم لتسجيل مستخدم محلي بدون الربط مع الدليل النشط',
    username: 'اسم المستخدم', // Users.UserNameLabelResource1_Text
    password: 'كلمة المرور', // Users.PasswordLabelResource1_Text
    confirmPassword: 'تأكيد كلمة المرور', // Users.ConfirmPasswordLabelResource1_Text
    email: 'البريد الإلكتروني', // Users.EmailLabelResource1_Text
    submit: 'إنشاء حساب مستخدم', // Sitemap.CreateUser_Title
    defaultDepartment: 'الإدارة الافتراضية',
    errors: {
      // Users_EmptyUsername, Users_WhiteSpacesUsername, Users_UsernameTooLong, Users_InvalidUsername
      usernameRequired: 'اسم المستخدم مطلوب',
      usernameSpaces: 'لا يمكن أن يحتوي اسم المستخدم على مسافات',
      usernameTooLong: (max) => `اسم المستخدم يجب ألا يزيد عن ${max} حرفا`,
      usernameChars: 'اسم المستخدم يقبل الحروف الإنجليزية والأرقام والرموز - . _ @ + فقط',
      passwordRequired: 'كلمة المرور مطلوبة',
      passwordFormat:
        'كلمة المرور يجب أن تكون من 8 إلى 64 حرفا، وتحتوي على حرف كبير وحرف صغير ورقم ورمز، بدون مسافات',
      confirmRequired: 'تأكيد كلمة المرور مطلوب',
      confirmMismatch: 'تأكيد كلمة المرور لا يتطابق مع كلمة المرور',
      emailRequired: 'البريد الإلكتروني مطلوب',
      emailFormat: 'من فضلك أدخل بريدا إلكترونيا صحيحا',
      arabicNameRequired: 'الاسم بالعربية مطلوب',
      englishNameRequired: 'الاسم بالإنجليزية مطلوب',
    },
  },

  /** #add_modal — mode B, `showCreationForm(true, type)` */
  importModal: {
    intro: 'يرجى رفع ملف بصيغة Excel (.xlsx) أو CSV (.csv)', // Resources.Please_upload_a_file_in_Excel
    columnsIntro: 'يجب أن يحتوي الملف على الأعمدة التالية',
    mandatory: 'إجباري', // Resources.Mandatory
    optional: 'اختياري', // Resources.Optional
    notesTitle: 'ملاحظات مهمة',
    notes:
      'يجب أن يحتوي الصف الأول في الملف على أسماء الأعمدة، وبنفس الأسماء والترتيب الموضح في الجدول',
    template: 'تحميل القالب', // was Resources.ViewTemplate; renamed 2026-10-22
    save: 'حفظ', // Resources.Save
    fileError: 'ملف إكسل او CSV', // Resources.ExcelOrCSV
  },

  /** Per-type configuration for the excel modal — js.cshtml:489-538. */
  importTypes: {
    UsersByEmail: {
      title: 'استيراد من ملف إكسل (محلي)', // Resources.ImportFromExcelLocal
      hint: 'هذه الخاصية تستخدم لتسجيل عدة مستخدمين محليين من خلال ملف الإكسل بدون ربط مع الدليل النشط',
      columns: [
        { name: 'Email', required: true },
        { name: 'FullName', required: false },
        { name: 'Department', required: false },
      ],
    },
    ActiveDirectoryUsers: {
      title: 'استيراد مستخدمين من LDAP بملف إكسل',
      hint: 'ارفع ملفا يحتوي على عمود Username فقط. يتم جلب البيانات والأقسام من مصدر LDAP المختار حسب ربط الحقول. الحسابات الموجودة لا تتغير ولا تستهلك مقاعد إضافية.',
      columns: [{ name: 'Username', required: true }],
    },
    ResignedUsers: {
      title: 'تحديث بيانات المستقيلين من ملف اكسل', // Resources.UpdateTheDataOfResigned
      hint: 'تستخدم هذه الخاصية لتحديث الاستقالة بشكل يدوي (تستخدم فقط في حال لم يتم تفعيل التزامن وتفعيل اكتشاف الاستقالات من مصدر الربط)',
      columns: [
        { name: 'Username', required: true },
        { name: 'Resignation Date (d/M/yyyy)', required: true },
      ],
    },
    ResetUsersPasswords: {
      title: 'إعادة تعيين كلمات مرور المستخدمين', // Resources.ResetPasswordForUsers
      hint: 'يمكن استخدام الخاصية لإعادة تعيين كلمات المرور للمستخدمين الحاليين',
      columns: [{ name: 'Username', required: true }],
    },
  },

  /** The rows the import refused — see `RejectedRowsModal`. */
  importRejected: {
    title: 'صفوف لم يتم استيرادها',
    lead: 'تم استيراد باقي الصفوف.',
    hint: 'راجع هذه الصفوف في الملف وصححها ثم اعد الاستيراد، او صدرها الى ملف اكسل لمراجعتها.',
    search: 'ابحث في الصفوف',
    count: (shown, total) => `${shown} من ${total}`,
    empty: 'لا يوجد صف يطابق البحث',
    /* The endpoint reports the FIRST CELL of each row it refused — a username, an email, or
       the literal `empty` for a blank row — so the column is named for what it holds. */
    nameHeader: 'القيمة',
    fileName: (type) => `صفوف لم يتم استيرادها - ${type}`,
    /* Short and constant: Excel rejects a sheet name over 31 characters or with : \ / ? * [ ] */
    sheetName: 'صفوف مرفوضة',
    exportFailed: 'تعذر تصدير الملف',
  },

  /** #edit_modal */
  edit: {
    title: 'تعديل البيانات الشخصية', // Users.btnEdit
    tabs: {
      profile: 'تعديل البيانات الشخصية',
      permissions: 'الصلاحيات',
      password: 'تغيير كلمة المرور', // Users.PageResource1_Title4
    },
    nameAr: 'الاسم بالعربية', // Users.SpanNameAr
    nameEn: 'الاسم بالإنجليزية', // Users.SpanNameEn
    group: 'الإدارة', // Users.SpanGroup
    gender: 'الجنس', // UserProfile.Label3Resource1_Text
    selectGender: 'اختر الجنس', // Resources.Select_Gender
    male: 'ذكر',
    female: 'أنثى',
    email: 'البريد الإلكتروني', // UserProfile.Label4Resource1_Text
    phone: 'رقم الهاتف', // UserProfile.Label5Resource1_Text
    mobile: 'رقم الجوال', // UserProfile.Label6Resource1_Text
    employeeId: 'رقم الموظف', // UserProfile.LabelEmployeeIdResource1_Text
    save: 'حفظ', // Resources.Save
    required: 'الحقل مطلوب', // Users.RequiredFieldValidator
    nameLetters: 'الاسم يجب أن يحتوي على حروف، ولا يكتفي بالرموز أو الأرقام',
    lengthError: 'المدخل لابد ألا يقل عن 3 أحرف و لا يزيد عن 200',
    phoneFormat: 'أدخل رقما صحيحا: أرقام فقط، ويمكن أن يبدأ بـ +',
    selectAllRoles: 'اختيار الكل', // Users.SelectAll
    updateRoles: 'تعديل', // Users.BtnUpdateData_Text
    roleUser: 'مستخدم عادي', // Resources.User
    roleAdmin: 'مدير النظام', // Resources.Admin
    autoPasswordHint: 'تغيير كلمة المرور تلقائيا وإرسالها إلى بريد المستخدم',
    autoPasswordButton: 'غير كلمة المرور', // Resources.Change_user_password
    manualPasswordHint: 'وضع كلمة المرور يدويا',
    newPassword: 'كلمة المرور',
    confirmNewPassword: 'تأكيد كلمة المرور',
    changePassword: 'تغيير كلمة المرور', // Users.PageResource1_Title4
    passwordMismatch: 'كلمة المرور لا تتطابق مع تأكيد كلمة المرور',
    /* Were hardcoded in the drawer's JSX; here so the whole tab reads from one
       place like every other string on this screen. */
    noPasswordChange: 'لا يمكن تغيير كلمة المرور',
    noPasswordChangeReason: 'لا يمكن تغيير كلمة المرور اذا كان نظام الدخول عبر الدليل النشط',
  },

  /** #edit_status_modal */
  status: {
    tabs: {
      approve: 'حالة الحساب',
      expiry: 'تاريخ انتهاء الحساب', // Resources.Account_Expiry
      locked: 'تعطيل الحساب لفترة',
      resigned: 'الاستقالة',
    },
    approved: 'نشط الحساب', // Users.ApproveCheckBoxResource1_Text
    notes: 'ملاحظات', // Resources.Notes
    update: 'تعديل', // Users.BtnUpdateData_Text
    save: 'حفظ', // Resources.Save
    expiryLabel: 'تاريخ انتهاء الحساب', // Resources.Account_Expiry
    lockoutLabel: 'تاريخ نهاية الايقاف', // Resources.Lockout_End_Date
    /* What an EMPTY value does, on the two fields where it is an instruction
       rather than a blank — the clear button is how you get there. */
    lockoutHint: 'اترك الحقل فارغا لرفع الإيقاف عن الحساب',
    /* Lifting a lockout is its own endpoint: it also resets the failed-attempt counter. */
    unlockNow: 'رفع الإيقاف الآن',
    lockedUntil: 'الحساب موقوف حتى',
    notLocked: 'الحساب غير موقوف حاليا.',
    fetchedResignation: 'تاريخ الاستقالة المسحوب من عملية التزامن',
    none: 'لا يوجد',
    resignationLabel: 'تاريخ الاستقالة', // Resources.Resignation_Date
    resignationHint: 'اترك الحقل فارغا لإلغاء الاستقالة',
  },

  deleteInactiveConfirm: (count) =>
    `سيتم حذف الحسابات المعروضة في هذه الصفحة (${count}) نهائيا. لا يمكن التراجع عن هذه العملية.`,
  selectAll: 'تحديد الكل', // Resources.Select_All
  close: 'إغلاق', // Users.btnClose
}
